import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  BulkFileUploadRejectedEvent,
  FlxBulkFileUploadComponent,
  FlxBulkUploadDeleteIconDirective,
  FlxBulkUploadDocumentIconDirective,
  FlxIconModule,
} from '@flowx/angular-ui-toolkit'
import {
  BulkFileUploadTemplateConfig,
  ProcessActionTriggerEvent,
  ResolvedAction,
  emitRejectedUploadAnalytics,
  getActionsByTemplateConfigAndTrigger$,
  getCurrentReadOnlyState,
  theme,
} from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal, getDynamicPropSignal, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-bulk-file-upload-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxBulkFileUploadComponent,
    FlxBulkUploadDocumentIconDirective,
    FlxBulkUploadDeleteIconDirective,
    FlxLocalizePipe,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './bulk-file-upload-wrapper.component.html',
  styleUrl: './bulk-file-upload-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxBulkFileUploadWrapperComponent implements OnInit, AfterViewInit {
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #destroyRef = inject(DestroyRef)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<BulkFileUploadTemplateConfig>()

  bulkFileUpload = viewChild(FlxBulkFileUploadComponent)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  icons = computed(() => ({
    titleIcon: getThemeIcon(ThemeComponent.BulkFileUpload, 'titleIcon'),
    documentIcon: getThemeIcon(ThemeComponent.BulkFileUpload, 'documentIcon'),
    deleteIcon: getThemeIcon(ThemeComponent.BulkFileUpload, 'deleteIcon'),
  }))

  // expressions
  isHidden: Signal<boolean>
  isDisabled: Signal<boolean>
  // dynamic props
  title: Signal<string>
  subtitle: Signal<string>
  browseLabel: Signal<string>
  resetLabel: Signal<string>
  uploadLabel: Signal<string>
  filesSizeExceeded: Signal<string>
  filesExceeded: Signal<string>
  invalidFileType: Signal<string>
  // theme class names for inner elements
  buttonClassName: string
  errorClassName: string
  // localized strings
  readonly deleteAriaLabel = '@@sys_a11y_bfu_delete_file'
  readonly maxFileSizeText = '@@sys_bfu_max_files_size'
  readonly dropFilesAriaLabel = '@@sys_a11y_bfu_drop_files'

  readonly = getCurrentReadOnlyState

  ngOnInit(): void {
    const theming = theme()
    this.buttonClassName = theming
      ? `${getComponentStyles(ThemeComponent.Button, theming)} flx-themed`
      : ''
    this.errorClassName = theming
      ? `${getComponentStyles(ThemeComponent.Error, theming)} flx-themed`
      : ''

    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const {
        title,
        subtitle,
        browseLabel,
        resetLabel,
        uploadLabel,
        filesSizeExceeded,
        filesExceeded,
        invalidFileType,
        hide,
        disabled,
      } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.isDisabled = getBoolExpressionSignal(disabled, this.templateConfig(), {
        logType: 'disabled',
      })
      this.title = getDynamicPropSignal(title, this.templateConfig())
      this.subtitle = getDynamicPropSignal(subtitle, this.templateConfig())
      this.browseLabel = getDynamicPropSignal(browseLabel, this.templateConfig())
      this.resetLabel = getDynamicPropSignal(resetLabel, this.templateConfig())
      this.uploadLabel = getDynamicPropSignal(uploadLabel, this.templateConfig())
      this.filesSizeExceeded = getDynamicPropSignal(filesSizeExceeded, this.templateConfig())
      this.filesExceeded = getDynamicPropSignal(filesExceeded, this.templateConfig())
      this.invalidFileType = getDynamicPropSignal(invalidFileType, this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  // Rejected by the toolkit: no request starts, so core never sees this batch.
  onFilesRejected({ files, errors }: BulkFileUploadRejectedEvent): void {
    emitRejectedUploadAnalytics(
      this.#clickActions().map(({ action }) => action),
      this.templateConfig(),
      files,
      errors,
      { bulk: true }
    )
  }

  async onFilesStartUpload(files: File[]): Promise<void> {
    if (this.isDisabled() || this.readonly() || this.#clickActions().length === 0) {
      return
    }

    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    try {
      await this.#actionService.runActions(this.#clickActions(), this.templateConfig(), formData)
      this.bulkFileUpload()?.clearFiles()
    } catch {
      // Upload failed (SSE UploadError) — keep files so user can retry
    }
  }
}
